'use client';

import { use } from 'react';

import { usePathname } from 'next/navigation';

import type { i18n as I18n } from 'i18next';
import { useTranslation } from 'react-i18next';

import { i18nNamespacesFor } from '~/lib/i18n/i18n.namespaces';

// One promise per pending load, so `use()` sees the same promise on every
// retry. Entries are dropped once settled: the loaded bundles answer later
// renders, and a re-initialised instance (language switch) loads afresh.
const pendingLoads = new Map<string, Promise<void>>();

function loadNamespaces(i18n: I18n, namespaces: string[]) {
  const key = `${i18n.language}|${namespaces.join(',')}`;
  let promise = pendingLoads.get(key);

  if (!promise) {
    promise = i18n
      .loadNamespaces(namespaces)
      .finally(() => pendingLoads.delete(key));

    pendingLoads.set(key, promise);
  }

  return promise;
}

/**
 * Makes sure the current area's translation namespaces are loaded.
 *
 * On a full page load they were already preloaded (the root layout picks the
 * same set), so this renders straight through. When a client-side navigation
 * enters another area (site → dashboard), it suspends inside the navigation
 * transition until that area's namespaces arrive, so the previous page stays
 * on screen instead of flashing untranslated keys.
 *
 * useTranslation(namespaces) can't do this: it uses the namespace array as a
 * hook dependency list, which must keep the same length between renders.
 */
export function I18nRouteNamespaces({
  isTenant,
  children,
}: React.PropsWithChildren<{ isTenant: boolean }>) {
  const { i18n } = useTranslation();
  const pathname = usePathname() ?? '';
  const missing = i18nNamespacesFor(pathname, { isTenant }).filter(
    (namespace) => !i18n.hasLoadedNamespace(namespace),
  );

  if (missing.length > 0) {
    use(loadNamespaces(i18n, missing));
  }

  return children;
}
